import type { LocationKind, LocationKindBehavior, LocationNode } from "../api/locations";
import type { ReactNode } from "react";
import type { SelectOption } from "../components/Select";

/**
 * Flat, depth-first projection of the location tree (`GET /locations/tree`)
 * for `Select` dropdowns — the location pickers on the asset, structure and
 * dismount forms all need the same indented list.
 */
export type FlatLocation = {
  id: string;
  name: string;
  code: string;
  kind: LocationKind;
  kindName: string;
  kindBehavior: LocationKindBehavior;
  depth: number;
  /** Root → parent ids (without the node itself). */
  ancestorIds: string[];
  /** "Obrenovac / Fabrika MCC2 / Magacin 1" */
  pathLabel: string;
};

/** Non-breaking spaces — plain spaces collapse inside Select option labels. */
export const LOCATION_INDENT = "   ";

export function flattenLocations(nodes: LocationNode[]): FlatLocation[] {
  const out: FlatLocation[] = [];
  const walk = (list: LocationNode[], depth: number, ancestors: string[], names: string[]) => {
    for (const n of list) {
      const path = [...names, n.name];
      out.push({
        id: n.id,
        name: n.name,
        code: n.code,
        kind: n.kind,
        kindName: n.kindName,
        kindBehavior: n.kindBehavior,
        depth,
        ancestorIds: ancestors,
        pathLabel: path.join(" / "),
      });
      walk(n.children, depth + 1, [...ancestors, n.id], path);
    }
  };
  walk(nodes, 0, [], []);
  return out;
}

/** Indented "Name (CODE)" options; `excludeSubtreeOf` drops that node and its descendants. */
export function locationSelectOptions(
  flat: FlatLocation[],
  opts: { excludeSubtreeOf?: string; left?: (e: FlatLocation) => ReactNode } = {},
): SelectOption<string>[] {
  const out: SelectOption<string>[] = [];
  for (const e of flat) {
    if (opts.excludeSubtreeOf && (e.id === opts.excludeSubtreeOf || e.ancestorIds.includes(opts.excludeSubtreeOf))) continue;
    out.push({ value: e.id, label: `${LOCATION_INDENT.repeat(e.depth)}${e.name} (${e.code})`, keywords: e.pathLabel, left: opts.left?.(e) });
  }
  return out;
}

export function findFlatLocation(flat: FlatLocation[], id: string | null | undefined): FlatLocation | null {
  if (!id) return null;
  return flat.find((e) => e.id === id) ?? null;
}
